import { useState } from 'react';
import coordId from './coordId';
import { formatCoord, shortId } from './format';

// A small chevron, drawn (not a glyph) so it renders identically everywhere.
const Chevron = ({ up }) => (
    <svg
        className="chevron"
        width="10" height="10" viewBox="0 0 10 10"
        fill="none" aria-hidden="true"
        style={up ? { transform: 'rotate(180deg)' } : undefined}
    >
        <path d="M1 3.5 L5 7 L9 3.5" stroke="currentColor" strokeWidth="1.3"
              strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// The signature element: a museum specimen label pinned to the current image.
// It shows the exact complex-plane region and its short id — the coordinates
// ARE the image's identity (the Phase 2 uniqueness key). "Unclaimed" is a
// dormant hook for the future claim/buy flow.
//
// Collapsible so it never permanently blocks part of the image on small screens
// (the label is interactive, so you can't start a drag through it). Collapsed,
// it stays as a chip showing the id — identity is always on screen.
const Placard = ({ current, manualZoom }) => {

    const [collapsed, setCollapsed] = useState(false);
    const [showCoords, setShowCoords] = useState(false);

    const { realMin, realMax, imaginaryMin, imaginaryMax } = current;
    const id = coordId(current);

    if (collapsed) {
        return (
            <button
                id="placard-chip"
                type="button"
                onClick={() => setCollapsed(false)}
                aria-label="Show specimen details"
            >
                <Chevron up />
                <span className="placard-label">Specimen</span>
                <span className="placard-id">{shortId(id)}</span>
            </button>
        );
    }

    return (
        <div id="placard">
            <div className="placard-head">
                <span className="placard-label">Specimen</span>
                <span className="placard-head-right">
                    <span className="placard-id">{shortId(id)}</span>
                    <button
                        type="button"
                        className="placard-hide"
                        onClick={() => setCollapsed(true)}
                        aria-label="Hide specimen details"
                    >
                        <Chevron />
                    </button>
                </span>
            </div>

            <div className="placard-row">
                <i className="placard-axis">&#8477;</i>
                <span className="placard-value">{formatCoord(realMin)} &rarr; {formatCoord(realMax)}</span>
            </div>
            <div className="placard-row">
                <i className="placard-axis">&#120050;</i>
                <span className="placard-value">{formatCoord(imaginaryMin)} &rarr; {formatCoord(imaginaryMax)}</span>
            </div>

            <div className="placard-foot">
                <span className="placard-tag">Unclaimed</span>
                <button
                    type="button"
                    className="placard-toggle"
                    aria-expanded={showCoords}
                    onClick={() => setShowCoords(s => !s)}
                >
                    {showCoords ? 'Close' : 'Enter coordinates'}
                </button>
            </div>

            {showCoords &&
                <div className="coord-form">
                    <label htmlFor="real-min-in">&#8477; min</label>
                    <input id="real-min-in" name="real-min-in" type="number" step="0.0001" placeholder={formatCoord(realMin)} />
                    <label htmlFor="real-max-in">&#8477; max</label>
                    <input id="real-max-in" name="real-max-in" type="number" step="0.0001" placeholder={formatCoord(realMax)} />
                    <label htmlFor="imaginary-min-in">&#120050; min</label>
                    <input id="imaginary-min-in" name="imaginary-min-in" type="number" step="0.0001" placeholder={formatCoord(imaginaryMin)} />
                    <label htmlFor="imaginary-max-in">&#120050; max</label>
                    <input id="imaginary-max-in" name="imaginary-max-in" type="number" step="0.0001" placeholder={formatCoord(imaginaryMax)} />
                    <button type="button" className="btn-render" onClick={() => manualZoom()}>Render</button>
                </div>
            }
        </div>
    );
};

export default Placard;
